import React, { useEffect, useState, useCallback } from 'react';
import { X, MessageCircle, ArrowLeft } from 'lucide-react';

const SESSION_KEY = 'trust_exit_intent_shown';

/**
 * ExitIntentPopup — desktop-only, fires once per session on mouse-leave (top edge).
 * Provides two low-friction actions: WhatsApp support (env-driven) or "back to offers".
 * NO discount codes (would break marketplace pricing).
 *
 * Props:
 *   enabled: bool (default true)
 *   whatsappNumber: string (optional, digits only, no +). Falls back to /kontakt link.
 *   t: optional translator.
 */
export function ExitIntentPopup({ enabled = true, whatsappNumber, t }) {
  const [visible, setVisible] = useState(false);
  const tr = (key, fallback) => (t ? (t(key) || fallback) : fallback);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === 'undefined') return undefined;
    // Desktop-only heuristic: skip touch devices & narrow viewports.
    const isDesktop = window.matchMedia && window.matchMedia('(min-width: 1024px)').matches
      && !('ontouchstart' in window);
    if (!isDesktop) return undefined;

    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { /* noop */ }
    if (alreadyShown) return undefined;

    const handler = (e) => {
      if (e.clientY <= 5 && !alreadyShown) {
        alreadyShown = true;
        setVisible(true);
      }
    };
    document.addEventListener('mouseout', handler);
    return () => document.removeEventListener('mouseout', handler);
  }, [enabled]);

  if (!visible) return null;

  const waHref = whatsappNumber
    ? `https://wa.me/${String(whatsappNumber).replace(/[^0-9]/g, '')}`
    : '/kontakt';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="exit-intent-popup"
      onClick={dismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"
          data-testid="exit-intent-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <h3 className="text-xl font-bold text-slate-900 mb-2" data-testid="exit-intent-title">
          {tr('trust_exit_title', 'Noch Fragen zu Ihrer Buchung?')}
        </h3>
        <p className="text-sm text-slate-600 mb-5">
          {tr('trust_exit_subtitle', 'Wir helfen Ihnen persönlich weiter – ohne Kaufdruck.')}
        </p>

        <div className="space-y-2.5">
          <a
            href={waHref}
            target={whatsappNumber ? '_blank' : undefined}
            rel="noreferrer"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors"
            data-testid="exit-intent-whatsapp"
          >
            <MessageCircle size={18} />
            {tr('trust_exit_whatsapp', 'WhatsApp-Support kontaktieren')}
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors"
            data-testid="exit-intent-back"
          >
            <ArrowLeft size={18} />
            {tr('trust_exit_back', 'Zurück zum Angebot')}
          </button>
        </div>
      </div>
    </div>
  );
}
